import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';

export default function LBMCalc() {
  const [tab, setTab] = useState(0);
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState(25);
  const [ft, setFt] = useState(5);
  const [inches, setInches] = useState(10);
  const [lb, setLb] = useState(165);
  const [cm, setCm] = useState(178);
  const [kg, setKg] = useState(75);
  const clear = () => { setGender('male'); setAge(25); setFt(5); setInches(10); setLb(165); setCm(178); setKg(75); };

  const c = useMemo(() => {
    const weightKg = tab === 0 ? (+lb || 0) * 0.453592 : +kg || 0;
    const heightCm = tab === 0 ? ((+ft || 0) * 12 + (+inches || 0)) * 2.54 : +cm || 0;
    const boer = gender === 'male' ? 0.407*weightKg + 0.267*heightCm - 19.2 : 0.252*weightKg + 0.473*heightCm - 48.3;
    const james = gender === 'male' ? 1.1*weightKg - 128*Math.pow(weightKg/heightCm, 2) : 1.07*weightKg - 148*Math.pow(weightKg/heightCm, 2);
    const hume = gender === 'male' ? 0.32810*weightKg + 0.33929*heightCm - 29.5336 : 0.29569*weightKg + 0.41813*heightCm - 43.2933;
    const disp = k => tab === 0 ? (k / 0.453592).toFixed(1) + ' lbs' : k.toFixed(1) + ' kg';
    return { boer, james, hume, disp };
  }, [tab, gender, age, ft, inches, lb, cm, kg]);

  return (
    <CalcShell look="cnet" title="Lean Body Mass Calculator" subtitle='Compute lean body mass — body weight minus fat mass — using several established formulas.'>
      <CalcNetPanel
        slug="lbm"
        activeTab={tab}
        onTabChange={setTab}
        onClear={clear}
        right={
          <div>
            <div className="text-sm text-slate-700 mb-2">Estimated Lean Body Mass:</div>
            <ResultTable
              testid="lbm-table"
              headers={['Formula', 'LBM']}
              rows={[
                ['Boer', <span className="font-bold text-[#2f9c3f]">{c.disp(c.boer)}</span>],
                ['James', c.disp(c.james)],
                ['Hume', c.disp(c.hume)],
              ]}
            />
          </div>
        }
      >
        <Row label="Gender"><RadioGroup name="lbm-g" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]} /></Row>
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="lbm-age" /></Row>
        {tab === 0 ? <>
          <Row label="Height"><div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={ft} inches={inches} onFt={setFt} onIn={setInches} testidPrefix="lbm-h" /></div></Row>
          <Row label="Weight"><UnitInput value={lb} onChange={setLb} unit="pounds" testid="lbm-wlb" /></Row>
        </> : <>
          <Row label="Height"><UnitInput value={cm} onChange={setCm} unit="cm" testid="lbm-hcm" /></Row>
          <Row label="Weight"><UnitInput value={kg} onChange={setKg} unit="kg" testid="lbm-wkg" /></Row>
        </>}
      </CalcNetPanel>

      <StepsPanel steps={[
        `Boer (${gender}): LBM = ${gender==='male'?'0.407·W + 0.267·H − 19.2':'0.252·W + 0.473·H − 48.3'}`,
        `LBM ≈ <b>${c.disp(c.boer)}</b>`,
      ]} />
    </CalcShell>
  );
}
